import { useTranslation } from 'react-i18next';
import { ClipboardCheck } from 'lucide-react';
import { Button } from '~/components/ui';
import type { ExtractedItem } from '~/api/documentExtraction';
import { MAX_ROWS } from '~/pages/inventory-import/lib/parseFile';
import {
  exceedsImportLimit,
  isFlagged,
  isUnnamed,
  type ImportHandoff,
} from './importHandoff';

export function ImportCard({
  items,
  importHandoff,
  onContinue,
}: {
  items: ExtractedItem[];
  importHandoff: ImportHandoff | null;
  onContinue: (handoff: ImportHandoff) => void;
}) {
  const { t } = useTranslation('document-extraction');
  const tooMany = importHandoff !== null && exceedsImportLimit(importHandoff);
  const heldBack = items.filter(isFlagged).length;
  const unnamed = items.filter(isUnnamed).length;
  const importRowCount =
    importHandoff === null ? 0 : importHandoff.parsedFile.rows.length;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex flex-col gap-1 text-sm text-neut-sec">
        <p>{t('import.hint', { count: importRowCount })}</p>
        {heldBack > 0 && (
          <p data-testid="held-back" className="text-warning-800">
            {t('import.held_back', { count: heldBack })}
          </p>
        )}
        {unnamed > 0 && (
          <p data-testid="unnamed" className="text-warning-800">
            {t('import.unnamed', { count: unnamed })}
          </p>
        )}
        {tooMany && (
          <p data-testid="too-many-rows" className="text-error-600">
            {t('import.too_many_rows', {
              rows: importRowCount,
              max: MAX_ROWS,
            })}
          </p>
        )}
      </div>
      <Button
        size="sm"
        width="auto"
        disabled={importHandoff === null || tooMany}
        onClick={() => importHandoff && onContinue(importHandoff)}
      >
        <ClipboardCheck size={16} />
        {t('import.continue')}
      </Button>
    </div>
  );
}
